import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { lovable } from '@/integrations/lovable';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { LogOut, BookMarked, Mail, Loader2, CreditCard } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { isInAppBrowser } from '@/utils/inAppBrowser';
import InAppBrowserBanner from '@/components/InAppBrowserBanner';
import { isAutomationMode } from '@/lib/automationMode';
import { pickByLang } from '@/i18n';
import { trackEvent } from '@/lib/analytics';

const GoogleIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const emailLabels: Record<string, {
  loginTitle: string; email: string; password: string;
  login: string; signup: string; or: string;
  google: string;
  magicLink: string; magicSent: string;
  switchToSignup: string; switchToLogin: string;
  forgotPassword: string; resetSent: string;
  signupSuccess: string; loginError: string;
}> = {
  ko: {
    loginTitle: '로그인',
    email: '이메일',
    password: '비밀번호',
    login: '로그인',
    signup: '회원가입',
    or: '또는',
    google: 'Google로 로그인',
    magicLink: '이메일 로그인 링크 받기',
    magicSent: '로그인 링크를 이메일로 보냈습니다.',
    switchToSignup: '계정이 없으신가요? 회원가입',
    switchToLogin: '이미 계정이 있으신가요? 로그인',
    forgotPassword: '비밀번호를 잊으셨나요?',
    resetSent: '비밀번호 재설정 메일을 보냈습니다.',
    signupSuccess: '가입 완료! 이메일을 확인해주세요.',
    loginError: '로그인에 실패했습니다.',
  },
  ja: {
    loginTitle: 'ログイン',
    email: 'メールアドレス',
    password: 'パスワード',
    login: 'ログイン',
    signup: '新規登録',
    or: 'または',
    google: 'Googleでログイン',
    magicLink: 'メールログインリンクを送信',
    magicSent: 'ログインリンクをメールで送信しました。',
    switchToSignup: 'アカウントをお持ちでない方',
    switchToLogin: 'アカウントをお持ちの方',
    forgotPassword: 'パスワードを忘れた方',
    resetSent: 'パスワードリセットメールを送信しました。',
    signupSuccess: '登録完了！メールをご確認ください。',
    loginError: 'ログインに失敗しました。',
  },
  vi: {
    loginTitle: 'Đăng nhập',
    email: 'Email',
    password: 'Mật khẩu',
    login: 'Đăng nhập',
    signup: 'Đăng ký',
    or: 'hoặc',
    google: 'Đăng nhập bằng Google',
    magicLink: 'Gửi liên kết đăng nhập qua email',
    magicSent: 'Đã gửi liên kết đăng nhập qua email.',
    switchToSignup: 'Chưa có tài khoản? Đăng ký',
    switchToLogin: 'Đã có tài khoản? Đăng nhập',
    forgotPassword: 'Quên mật khẩu?',
    resetSent: 'Email đặt lại mật khẩu đã được gửi.',
    signupSuccess: 'Đăng ký thành công! Vui lòng kiểm tra email.',
    loginError: 'Đăng nhập thất bại.',
  },
  th: {
    loginTitle: 'เข้าสู่ระบบ',
    email: 'อีเมล',
    password: 'รหัสผ่าน',
    login: 'เข้าสู่ระบบ',
    signup: 'สมัครสมาชิก',
    or: 'หรือ',
    google: 'เข้าสู่ระบบด้วย Google',
    magicLink: 'ส่งลิงก์เข้าสู่ระบบทางอีเมล',
    magicSent: 'ส่งลิงก์เข้าสู่ระบบไปที่อีเมลแล้ว',
    switchToSignup: 'ยังไม่มีบัญชี? สมัครสมาชิก',
    switchToLogin: 'มีบัญชีแล้ว? เข้าสู่ระบบ',
    forgotPassword: 'ลืมรหัสผ่าน?',
    resetSent: 'ส่งอีเมลรีเซ็ตรหัสผ่านแล้ว',
    signupSuccess: 'สมัครสำเร็จ! กรุณาตรวจสอบอีเมล',
    loginError: 'เข้าสู่ระบบล้มเหลว',
  },
  id: {
    loginTitle: 'Masuk',
    email: 'Email',
    password: 'Kata sandi',
    login: 'Masuk',
    signup: 'Daftar',
    or: 'atau',
    google: 'Masuk dengan Google',
    magicLink: 'Kirim tautan masuk via email',
    magicSent: 'Tautan masuk telah dikirim ke email.',
    switchToSignup: 'Belum punya akun? Daftar',
    switchToLogin: 'Sudah punya akun? Masuk',
    forgotPassword: 'Lupa kata sandi?',
    resetSent: 'Email reset kata sandi telah dikirim.',
    signupSuccess: 'Pendaftaran berhasil! Periksa email Anda.',
    loginError: 'Gagal masuk.',
  },
  en: {
    loginTitle: 'Sign In',
    email: 'Email',
    password: 'Password',
    login: 'Sign In',
    signup: 'Sign Up',
    or: 'or',
    google: 'Continue with Google',
    magicLink: 'Send magic login link',
    magicSent: 'Magic login link sent to your email.',
    switchToSignup: 'No account yet? Sign up',
    switchToLogin: 'Already have an account? Sign in',
    forgotPassword: 'Forgot your password?',
    resetSent: 'Password reset email sent.',
    signupSuccess: 'Sign-up complete. Please check your email.',
    loginError: 'Failed to sign in.',
  },
  ms: {
    loginTitle: 'Log Masuk',
    email: 'E-mel',
    password: 'Kata laluan',
    login: 'Log Masuk',
    signup: 'Daftar',
    or: 'atau',
    google: 'Log masuk dengan Google',
    magicLink: 'Hantar pautan log masuk e-mel',
    magicSent: 'Pautan log masuk telah dihantar ke e-mel.',
    switchToSignup: 'Belum ada akaun? Daftar',
    switchToLogin: 'Sudah ada akaun? Log masuk',
    forgotPassword: 'Lupa kata laluan?',
    resetSent: 'E-mel tetapan semula kata laluan telah dihantar.',
    signupSuccess: 'Pendaftaran berjaya. Sila semak e-mel anda.',
    loginError: 'Gagal log masuk.',
  },
  'pt-BR': {
    loginTitle: 'Entrar',
    email: 'E-mail',
    password: 'Senha',
    login: 'Entrar',
    signup: 'Cadastrar',
    or: 'ou',
    google: 'Entrar com Google',
    magicLink: 'Enviar link de acesso por e-mail',
    magicSent: 'Link de acesso enviado para seu e-mail.',
    switchToSignup: 'Ainda não tem conta? Cadastre-se',
    switchToLogin: 'Já tem conta? Entre',
    forgotPassword: 'Esqueceu sua senha?',
    resetSent: 'E-mail de redefinição de senha enviado.',
    signupSuccess: 'Cadastro concluído. Verifique seu e-mail.',
    loginError: 'Falha ao entrar.',
  },
  my: {
    loginTitle: 'လော့ဂ်အင်',
    email: 'အီးမေးလ်',
    password: 'စကားဝှက်',
    login: 'လော့ဂ်အင်',
    signup: 'စာရင်းသွင်းရန်',
    or: 'သို့မဟုတ်',
    google: 'Google ဖြင့် ဝင်မည်',
    magicLink: 'အီးမေးလ်လော့ဂ်အင်လင့်ခ်ပို့မည်',
    magicSent: 'လော့ဂ်အင်လင့်ခ်ကို အီးမေးလ်သို့ပို့ပြီးပါပြီ။',
    switchToSignup: 'အကောင့်မရှိသေးပါသလား? စာရင်းသွင်းပါ',
    switchToLogin: 'အကောင့်ရှိပြီးပြီလား? လော့ဂ်အင်ဝင်ပါ',
    forgotPassword: 'စကားဝှက်မေ့သွားပြီလား?',
    resetSent: 'စကားဝှက်ပြန်သတ်မှတ်ရန် အီးမေးလ် ပို့ပြီးပါပြီ။',
    signupSuccess: 'စာရင်းသွင်းခြင်းပြီးပါပြီ။ အီးမေးလ်ကို စစ်ဆေးပါ။',
    loginError: 'လော့ဂ်အင် မအောင်မြင်ပါ။',
  },
  de: {
    loginTitle: 'Anmelden',
    email: 'E-Mail',
    password: 'Passwort',
    login: 'Anmelden',
    signup: 'Registrieren',
    or: 'oder',
    google: 'Mit Google anmelden',
    magicLink: 'Magic-Link per E-Mail senden',
    magicSent: 'Magic-Link an Ihre E-Mail gesendet.',
    switchToSignup: 'Noch kein Konto? Registrieren',
    switchToLogin: 'Bereits ein Konto? Anmelden',
    forgotPassword: 'Passwort vergessen?',
    resetSent: 'E-Mail zum Zurücksetzen gesendet.',
    signupSuccess: 'Registrierung abgeschlossen. Bitte E-Mail prüfen.',
    loginError: 'Anmeldung fehlgeschlagen.',
  },
  es: {
    loginTitle: 'Iniciar sesión',
    email: 'Correo electrónico',
    password: 'Contraseña',
    login: 'Iniciar sesión',
    signup: 'Registrarse',
    or: 'o',
    google: 'Continuar con Google',
    magicLink: 'Enviar enlace mágico por correo',
    magicSent: 'Enlace mágico enviado a su correo.',
    switchToSignup: '¿No tienes cuenta? Regístrate',
    switchToLogin: '¿Ya tienes cuenta? Inicia sesión',
    forgotPassword: '¿Olvidaste tu contraseña?',
    resetSent: 'Correo de restablecimiento enviado.',
    signupSuccess: 'Registro completado. Revisa tu correo.',
    loginError: 'Error al iniciar sesión.',
  },
  fr: {
    loginTitle: 'Connexion',
    email: 'E-mail',
    password: 'Mot de passe',
    login: 'Connexion',
    signup: 'Inscription',
    or: 'ou',
    google: 'Continuer avec Google',
    magicLink: 'Envoyer un lien magique par e-mail',
    magicSent: 'Lien magique envoyé à votre e-mail.',
    switchToSignup: 'Pas de compte ? Inscrivez-vous',
    switchToLogin: 'Déjà un compte ? Connectez-vous',
    forgotPassword: 'Mot de passe oublié ?',
    resetSent: 'E-mail de réinitialisation envoyé.',
    signupSuccess: 'Inscription terminée. Vérifiez votre e-mail.',
    loginError: 'Échec de la connexion.',
  },
  ar: {
    loginTitle: 'تسجيل الدخول',
    email: 'البريد الإلكتروني',
    password: 'كلمة المرور',
    login: 'تسجيل الدخول',
    signup: 'إنشاء حساب',
    or: 'أو',
    google: 'المتابعة مع Google',
    magicLink: 'إرسال رابط تسجيل الدخول بالبريد',
    magicSent: 'تم إرسال رابط تسجيل الدخول إلى بريدك.',
    switchToSignup: 'ليس لديك حساب؟ سجّل الآن',
    switchToLogin: 'لديك حساب؟ سجّل الدخول',
    forgotPassword: 'نسيت كلمة المرور؟',
    resetSent: 'تم إرسال بريد إعادة تعيين كلمة المرور.',
    signupSuccess: 'اكتمل التسجيل. تحقق من بريدك.',
    loginError: 'فشل تسجيل الدخول.',
  },
  'zh-TW': {
    loginTitle: '登入',
    email: '電子郵件',
    password: '密碼',
    login: '登入',
    signup: '註冊',
    or: '或',
    google: '使用 Google 登入',
    magicLink: '發送電子郵件登入連結',
    magicSent: '已發送登入連結至電子郵件。',
    switchToSignup: '還沒有帳號？註冊',
    switchToLogin: '已有帳號？登入',
    forgotPassword: '忘記密碼？',
    resetSent: '已發送密碼重置郵件。',
    signupSuccess: '註冊成功！請檢查電子郵件。',
    loginError: '登入失敗。',
  },
  'zh-CN': {
    loginTitle: '登录',
    email: '电子邮件',
    password: '密码',
    login: '登录',
    signup: '注册',
    or: '或',
    google: '使用 Google 登录',
    magicLink: '发送邮箱登录链接',
    magicSent: '已发送登录链接到邮箱。',
    switchToSignup: '还没有账号？注册',
    switchToLogin: '已有账号？登录',
    forgotPassword: '忘记密码？',
    resetSent: '已发送密码重置邮件。',
    signupSuccess: '注册成功！请检查电子邮件。',
    loginError: '登录失败。',
  },
};

const AuthButton = () => {
  const { user, loading, signOut } = useAuth();
  const { t, lang, localePath } = useI18n();
  const navigate = useNavigate();
  const [showDialog, setShowDialog] = useState(false);
  const [isSignup, setIsSignup] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sendingMagicLink, setSendingMagicLink] = useState(false);

  const inApp = isInAppBrowser();
  const automationMode = isAutomationMode();
  const el = pickByLang(emailLabels, lang);

  // Allow other components (e.g. SignupGateModal) to open the auth dialog.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { signup?: boolean } | undefined;
      setIsSignup(detail?.signup ?? true);
      setShowDialog(true);
      trackSignupView('signup_gate');
    };
    window.addEventListener('pt:open-auth', handler);
    return () => window.removeEventListener('pt:open-auth', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const trackSignupView = (source: string) => {
    import('@/lib/funnelTracker').then(({ trackFunnel }) => {
      trackFunnel('signup_view', { source, lang }, { onceSession: true });
    });
  };

  const handleGoogleLogin = async () => {
    trackEvent('auth_google_click', { lang, inApp });
    trackSignupView('google');
    if (inApp) {
      setShowDialog(true);
      return;
    }
    await lovable.auth.signInWithOAuth('google', {
      redirect_uri: window.location.origin,
    });
  };

  const handleShowEmailDialog = () => {
    trackEvent('auth_email_dialog_open', { lang });
    trackSignupView('email_dialog');
    setShowDialog(true);
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast.info(el.email);
      return;
    }
    setSubmitting(true);
    trackEvent('auth_email_submit', { lang, mode: isSignup ? 'signup' : 'login' });
    try {
      if (isSignup) {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: window.location.origin },
        });
        if (error) throw error;
        toast.success(el.signupSuccess);
        trackEvent('auth_signup_success', { lang });
        import('@/lib/funnelTracker').then(({ trackFunnel }) => {
          trackFunnel('signup_complete', { method: 'email', lang });
        });
        setShowDialog(false);
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        trackEvent('auth_login_success', { lang });
        setShowDialog(false);
      }
    } catch (err: any) {
      toast.error(err.message || el.loginError);
      trackEvent('auth_email_error', { lang, mode: isSignup ? 'signup' : 'login' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgotPassword = async () => {
    trackEvent('auth_forgot_password_click', { lang });
    if (!email) {
      toast.info(el.email);
      return;
    }
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) {
      toast.error(error.message);
    } else {
      toast.success(el.resetSent);
    }
  };

  const handleMagicLinkLogin = async () => {
    trackEvent('auth_magic_link_click', { lang });
    if (!email) {
      toast.info(el.email);
      return;
    }

    setSendingMagicLink(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: window.location.origin },
      });
      if (error) throw error;
      toast.success(el.magicSent);
      setShowDialog(false);
    } catch (err: any) {
      toast.error(err.message || el.loginError);
      trackEvent('auth_magic_link_error', { lang });
    } finally {
      setSendingMagicLink(false);
    }
  };

  if (loading) return null;

  if (!user) {
    const isChina = lang === 'zh-CN';
    return (
      <>
        <div className="flex items-center gap-1">
          {isChina ? (
            <Button
              variant="outline"
              size="sm"
              onClick={handleShowEmailDialog}
              className="gap-2 rounded-xl border-2 font-medium"
            >
              <Mail className="w-4 h-4" />
              {el.login}
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleGoogleLogin}
                className="gap-2 rounded-xl border-2 font-medium"
              >
                <GoogleIcon />
                {t.loginButton}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleShowEmailDialog}
                className="gap-1 text-xs text-muted-foreground px-2"
                aria-label="Email"
              >
                <Mail className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Email</span>
              </Button>
            </>
          )}
        </div>

        {/* Email login dialog — shown in in-app browsers or when user requests */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>{el.loginTitle}</DialogTitle>
            </DialogHeader>

            {inApp && !automationMode && <InAppBrowserBanner />}

              {!inApp && (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full gap-2"
                  onClick={handleGoogleLogin}
                >
                  <GoogleIcon />
                  {el.google}
                </Button>
              )}

            <div className="relative my-3">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">{el.or}</span>
              </div>
            </div>

            <div className="space-y-2">
              <Input
                type="email"
                placeholder={el.email}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Button
                type="button"
                variant="secondary"
                className="w-full gap-2"
                onClick={handleMagicLinkLogin}
                disabled={sendingMagicLink}
              >
                {sendingMagicLink && <Loader2 className="w-4 h-4 animate-spin" />}
                <Mail className="w-4 h-4" />
                {el.magicLink}
              </Button>
            </div>

            <div className="relative my-1">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-[10px] uppercase">
                <span className="bg-background px-2 text-muted-foreground">{el.or}</span>
              </div>
            </div>

            <form onSubmit={handleEmailAuth} className="space-y-3">
              <Input
                type="password"
                placeholder={el.password}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
              />
              <Button type="submit" className="w-full gap-2" disabled={submitting}>
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                <Mail className="w-4 h-4" />
                {isSignup ? el.signup : el.login}
              </Button>
            </form>

            {!isSignup && (
              <button
                type="button"
                onClick={handleForgotPassword}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors w-full text-center"
              >
                {el.forgotPassword}
              </button>
            )}

            <button
              type="button"
              onClick={() => setIsSignup(!isSignup)}
              className="text-xs text-primary hover:underline w-full text-center"
            >
              {isSignup ? el.switchToLogin : el.switchToSignup}
            </button>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  const displayName = user.user_metadata?.full_name || user.user_metadata?.name || user.email || '';
  const avatarUrl = user.user_metadata?.avatar_url || user.user_metadata?.picture || '';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2">
          <Avatar className="w-6 h-6">
            <AvatarImage src={avatarUrl} alt={displayName} />
            <AvatarFallback className="text-xs">{displayName.charAt(0).toUpperCase()}</AvatarFallback>
          </Avatar>
          <span className="hidden sm:inline text-sm">{displayName}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => navigate(localePath('/wrong-answers'))}>
          <BookMarked className="w-4 h-4 mr-2" />
          {t.wrongAnswers}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate(localePath('/my-subscription'))}>
          <CreditCard className="w-4 h-4 mr-2" />
          {t.navSubscription}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={signOut}>
          <LogOut className="w-4 h-4 mr-2" />
          {t.logout}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AuthButton;
